<template>
	<div class="step3">
		<div class="step3__footer">
			<button class="btn-beltone xl" @click="prevStep()">Back</button>
			<button class="btn-beltone xl" :class="{'disabled': !canContinue}" @click="nextStep()" >Continue</button>
		</div>
		<div class="step3__content">
			<label class="step3__header">Personal Information</label>
			<div class="step3__form">
        <input placeholder="First Name*" 
               type="text" 
               tabindex="1" 
               required 
               class="step3__textInput"
               :class="{'error': !validate.firstName}" 
               @focus="validate.firstName = true" 
               v-model="patient.firstName">
        <input placeholder="Last Name*" 
               type="text" 
               tabindex="2" 
               required
               class="step3__textInput" 
               :class="{'error': !validate.lastName}" 
               @focus="validate.lastName = true" 
               v-model="patient.lastName">
        <input placeholder="Email Address*" 
               type="email" 
               tabindex="3" 
               required
               class="step3__textInput"
               :class="{'error': !validate.emailAddress}" 
               @focus="validate.emailAddress = true" 
               v-model="patient.emailAddress">
        <input placeholder="Phone Number*" 
               type="tel" 
               tabindex="4" 
               required
               class="step3__textInput" 
               :class="{'error': !validate.phoneNumber}" 
               @focus="validate.phoneNumber = true" 
               v-mask="'(###) ###-####'" 
               v-model="patient.phoneNumber">

				<p class="familiar-voice-notice">It is important that someone with a familiar voice accompany you to the appointment, because we like to use their familiar voice during the evaluation.</p>

				<div class="step3__radios mt-4">					
					<label class="step3__radios-title">Are you bringing a Familiar Voice to your appointment?</label>
					<div class="step3__radios-selector" :class="{'error': !validate.bringingFamiliarVoice}">
						<div class="step3__radios-group">
							<input @click="validate.bringingFamiliarVoice = true" 
                     class="step3__radios-input" 
                     :id="familiarVoiceYesId" 
                     :name="familiarVoiceName" 
                     type="radio"
                     tabindex="5" 
                     :value="true" 
                     v-model="patient.bringingFamiliarVoice">
              <label :for="familiarVoiceYesId" class="step3__radios-label">
                <div class="step3__radios-filament"/>
              </label>
							<label :for="familiarVoiceYesId" class="step3__radios-text">Yes</label>
						</div>
						<div class="step3__radios-group">
							<input @click="validate.bringingFamiliarVoice = true" 
                     class="step3__radios-input"
                     :id="familiarVoiceNoId" 
                     :name="familiarVoiceName" 
                     type="radio" 
                     tabindex="6" 
                     :value="false"
                     v-model="patient.bringingFamiliarVoice">
							<label :for="familiarVoiceNoId" class="step3__radios-label">
                <div class="step3__radios-filament"/>
              </label>
							<label :for="familiarVoiceNoId" class="step3__radios-text">No</label>
						</div>
					</div>
				</div>
				<template v-if="patient.bringingFamiliarVoice">
          <input placeholder="Familiar Voice First Name*" 
                 type="text" 
                 tabindex="7" 
                 required
                 class="step3__textInput"
                 :class="{'error': !validate.familiarVoiceFirstName}" 
                 @focus="validate.familiarVoiceFirstName = true" 
                 v-model="patient.familiarVoiceFirstName">
          <input placeholder="Familiar Voice Last Name*"
               type="text"
               tabindex="8"
               required
                 class="step3__textInput"
                 :class="{'error': !validate.familiarVoiceLastName}"
               @focus="validate.familiarVoiceLastName = true"
               v-model="patient.familiarVoiceLastName">
          <select tabindex="9" 
                  required 
                  class="step3__select"
                  :class="{'error': !validate.relation}" 
                  @focus="validate.relation = true" 
                  v-model="patient.relation">
            <option value="" disabled selected hidden>Choose the Relation</option>
            <option v-for="item in companionTypes" :value="item"> {{ item.name }}</option>
          </select>
				</template>
				<div v-if="!hideSmsConsent" class="step3__agreement checkbox">
					<input type="checkbox" :id="smsConsentId" v-model="accept"/>
					<label :for="smsConsentId">By checking this box I agree to I agree to receive SMS from {{ smsConsentBrandName }}. Standard text messaging rates apply.  I can opt out at any time. </label>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import Services from '../../mixins/services';

export default {
	mixins: [Services],
  props: {
    hideSmsConsent: {
      type: Boolean,
      default: false,
    },
  },
	data: function () {
		return {
			valid: true,
			accept: false,
			validate: {
				firstName: true,
				lastName: true,
				emailAddress: true,
				phoneNumber: true,
				bringingFamiliarVoice: true,
				familiarVoiceFirstName: true,
				familiarVoiceLastName: true,
				relation: true,
			},
			patient: {
				firstName: '',
				lastName: '',
				emailAddress: '',
				phoneNumber: '',
				bringingFamiliarVoice: false,
				familiarVoiceFirstName: '',
				familiarVoiceLastName: '',
				relation: '',
			}
		}
	},
	computed: {
    ...mapState({
      companionTypes: state => state.OnlineScheduling.companionTypes,
      savedPatient: state => state.OnlineScheduling.patient,
      clientToken: state => state.ClientToken.clientToken
    }),
    canContinue() {
      return this.hideSmsConsent || this.accept;
    },
    familiarVoiceName() {
      return `familiarVoice-${this._uid}`;
    },
    familiarVoiceYesId() {
      return `${this.familiarVoiceName}-yes`;
    },
    familiarVoiceNoId() {
      return `${this.familiarVoiceName}-no`;
    },
    smsConsentId() {
      return `smsConsent-${this._uid}`;
    },
    smsConsentBrandName() {
      const host = this.getCurrentHost();
      const brandByHost = {
        'beltonealliance.com': 'Beltone Alliance',
        'www.beltonealliance.com': 'Beltone Alliance',
        'beltonearizona.com': 'Beltone Arizona',
        'www.beltonearizona.com': 'Beltone Arizona',
        'georgiabeltone.com': 'Beltone Georgia',
        'www.georgiabeltone.com': 'Beltone Georgia',
        'beltonegeorgia.com': 'Beltone Georgia',
        'www.beltonegeorgia.com': 'Beltone Georgia',
        'beltonemichigan.com': 'Beltone Michigan',
        'www.beltonemichigan.com': 'Beltone Michigan',
        'blackdiamond2022.com': 'Beltone Michigan',
        'www.blackdiamond2022.com': 'Beltone Michigan',
      };

      if (brandByHost[host]) return brandByHost[host];
      if (host.includes('arizona')) return 'Beltone Arizona';
      if (host.includes('georgia')) return 'Beltone Georgia';
      if (host.includes('michigan')) return 'Beltone Michigan';

      return 'Beltone Alliance';
    },
  },
  watch: {
    patient: {
      deep: true,
      handler(value) {
        this.setStateValue({ key: 'patient', value });
      },
    },
  },
	methods: {
    ...mapMutations('OnlineScheduling', { setStateValue: 'setStateValue' }),
    getCurrentHost() {
      if (typeof window === 'undefined' || !window.location) return '';

      return window.location.hostname.toLowerCase();
    },
		prevStep() {
      this.setStateValue({ key: 'patient', value: this.patient });
      this.setStateValue({ key: 'step', value: 2 });
    },
		nextStep() {
			if (this.canContinue && this.validation()) {
        this.setStateValue({ key: 'patient', value: this.patient });
        this.setStateValue({ key: 'step', value: 4 });
      }
		},
		validation() {
			let valid = true;
      let emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
			let phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im

			if (this.patient.firstName.trim().length === 0) {
				this.validate.firstName = false;
				valid = false;
			}
			if (this.patient.lastName.trim().length === 0) {
				this.validate.lastName = false;
				valid = false;
			}
			if (!emailRegex.test(this.patient.emailAddress)) {
				this.validate.emailAddress = false;
				valid = false;
			}
			if (!phoneRegex.test(this.patient.phoneNumber)) {
				this.validate.phoneNumber = false;
				valid = false;
			}
			if (this.patient.bringingFamiliarVoice === null) {
				this.validate.bringingFamiliarVoice = false;
				valid = false;
			}
			if (this.patient.bringingFamiliarVoice) {
				if (this.patient.familiarVoiceFirstName.trim().length === 0) {
					this.validate.familiarVoiceFirstName = false;
					valid = false;
				}
				if (this.patient.familiarVoiceLastName.trim().length === 0) {
					this.validate.familiarVoiceLastName = false;
					valid = false;
				}
				if (!this.patient.relation) {
					this.validate.relation = false;
					valid = false;
				}
			}
			return valid;
		}
	},
  mounted() {
    if (!this.savedPatient) return;

    this.patient = {
      ...this.patient,
      ...this.savedPatient,
    };
  }
}
</script>
